import { CheckCircle2, Info, TrendingUp } from "lucide-react";
import type { LatestReading, SiteInfo } from "@spf/contracts";
import type { WidgetId } from "../../lib/preferences.js";
import { Modal } from "../Modal.js";
import { formatAge, formatDateTime, formatUnit } from "../../lib/format.js";

interface Detail {
  title: string;
  subtitle: string;
  facts: string[];
  note: string;
}

/**
 * The design's widget detail sheet: an eyebrow, a title and subtitle, a list
 * of specifications, and a highlighted note at the foot.
 *
 * The design fills that shape with fiction. Its battery entry claims a 13.5
 * kWh LiFePO4 pack at 99.4% health after 184 cycles with 18 milliohms of
 * internal resistance; its flow entry claims 5.6 kW of photovoltaic ingress
 * and 98.4% MPPT efficiency. None of it comes from anywhere -- it is a
 * constant table in the component -- and on a real installation every line of
 * it would be a lie told with a checkmark beside it.
 *
 * So the shape is kept and every line is derived from the reading that is on
 * screen. Where a fact is genuinely unavailable it says so in words, which is
 * the whole reason this sheet is worth having: it is where a reader goes to
 * ask "where does that number come from", and the answer has to be true.
 */
export function WidgetDetailModal({
  id,
  value,
  site,
  ageSeconds,
  onClose,
}: {
  id: WidgetId;
  value: LatestReading;
  site: SiteInfo | null;
  ageSeconds: number | null;
  onClose: () => void;
}) {
  const detail = detailFor(id, value, site, ageSeconds);

  return (
    <Modal title={detail.title} onClose={onClose} className="md:max-w-2xl">
      <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">
        <Info size={14} aria-hidden="true" className="text-accent-ink" />
        Where this comes from
      </p>
      <p className="mb-6 text-sm leading-relaxed text-ink-muted">{detail.subtitle}</p>

      <ul className="space-y-3 border-y border-line py-4">
        {detail.facts.map((fact) => (
          <li key={fact} className="flex items-start gap-3">
            <CheckCircle2
              size={16}
              aria-hidden="true"
              className="mt-0.5 shrink-0 text-accent-2-ink"
            />
            <span className="text-sm font-medium text-ink">{fact}</span>
          </li>
        ))}
      </ul>

      <p className="mt-6 flex items-center gap-3 rounded-2xl border border-line bg-line/40 p-4 text-xs text-ink-muted">
        <TrendingUp size={20} aria-hidden="true" className="shrink-0 text-accent-ink" />
        <span>{detail.note}</span>
      </p>
    </Modal>
  );
}

function detailFor(
  id: WidgetId,
  value: LatestReading,
  site: SiteInfo | null,
  ageSeconds: number | null,
): Detail {
  const reading = value.reading;
  const outlook = value.battery;
  const taken =
    reading === null
      ? "no reading yet"
      : ageSeconds === null
        ? formatDateTime(reading.time)
        : `${formatAge(ageSeconds)}, at ${formatDateTime(reading.time)}`;
  const source = `Taken from the reading ${taken}.`;

  switch (id) {
    case "welcome":
      return {
        title: "Site and mode",
        subtitle: "Which site this instance is configured for, and what the inverter is doing.",
        facts: [
          `Site name: ${site?.siteName ?? "not loaded"}`,
          `Timezone: ${site?.timezone ?? "not loaded"}`,
          reading === null
            ? "Battery direction: no reading"
            : `Battery direction: ${directionOf(reading.batteryPowerW)}`,
          "The two round chips report the mode. They are not controls: this system never writes to the inverter.",
        ],
        note: source,
      };

    case "estFull":
      return {
        title: "Estimated full",
        subtitle: "How long until the bank is full at the rate it is charging at now.",
        facts: [
          outlook === null || outlook.timeToFullHours === null
            ? "Time to full: not charging, so there is nothing to count down to"
            : `Time to full: ${formatUnit(outlook.timeToFullHours, "h")}`,
          outlook === null
            ? "Capacity: not configured"
            : `Capacity it is filling: ${formatUnit(outlook.capacityKwh, "kWh")}`,
          "Worked out on the server, not here. It depends on a capacity the inverter never reports.",
          "The bars are decoration and carry no measurement.",
        ],
        note: source,
      };

    case "battery":
      return {
        title: "Battery status",
        subtitle: "The charge the inverter reports, and what it means in kilowatt-hours.",
        facts: [
          reading === null ? "Charge: no reading" : `Charge: ${formatUnit(reading.socPct, "%")}`,
          reading === null
            ? "Terminal voltage: no reading"
            : `Terminal voltage: ${formatUnit(reading.batteryVoltageV, "V")}`,
          outlook === null
            ? "Usable energy: capacity not configured"
            : `Usable above the ${String(outlook.reserveSocPct)}% floor: ${formatUnit(outlook.usableKwh, "kWh")}`,
          "State of health, cycle count and per-cell voltages are not reported by this inverter.",
        ],
        note: source,
      };

    case "totalEnergy":
      return {
        title: "Energy summary",
        subtitle: "Energy delivered to the house since midnight on the site's clock.",
        facts: [
          "Worked out on the server from every reading stored today. A stretch with no readings is left out and flagged, never guessed at.",
          reading === null
            ? "The inverter's own count of house load from the grid today: no reading"
            : `The inverter's own count of house load from the grid today: ${formatUnit(reading.acOutputTodayKwh, "kWh")}`,
          "That counter covers only what the grid supplied, so it is not the figure on the card.",
          "There is no daily target configured, so the bar has nothing to fill towards.",
        ],
        note: "The inverter's counter resets at midnight on its own clock, which is set by hand.",
      };

    case "loadBalance":
      return {
        title: "Load balance",
        subtitle: "How much of what the inverter can carry the house is using.",
        facts: [
          reading === null
            ? "In use: no reading"
            : `In use: ${formatUnit(reading.loadPercent, "%")}`,
          reading === null
            ? "Drawn: no reading"
            : `Drawn right now: ${formatUnit(reading.loadPowerW, "W", true)}`,
          "This inverter is single phase. There is one leg to watch rather than two to balance.",
        ],
        note: source,
      };

    case "mainChart":
      return {
        title: "Energy history",
        subtitle: "Generation day by day, once there is a stored series to draw.",
        facts: [
          "Readings are stored every five minutes and nothing rolls them up yet.",
          "The chart is built and takes its points as a prop, so the aggregates slice fills it in.",
          "The y scale comes from the data rather than a fixed ceiling.",
        ],
        note: "Nothing is drawn rather than something being estimated.",
      };

    case "earning":
      return {
        title: "Grid cost",
        subtitle: "What today's import from the grid cost, and how much of it charged the battery.",
        facts: [
          `Currency: ${site?.currency ?? "not loaded"}`,
          "Each quarter hour is priced at the tariff in force when it started, VAT included.",
          "The battery's share is what the meter billed for charging it, the charger's own loss included.",
          "Where no tariff covers the day, the card says so rather than showing a zero.",
        ],
        note: "Tariffs are entered under Settings. Adding an earlier one prices the days before it.",
      };

    case "weather":
      return {
        title: "Weather",
        subtitle: "Conditions over the installation.",
        facts: [
          "No weather source is configured for this deployment.",
          "The production content-security policy blocks the browser from calling a third party directly, so this would be served through the API.",
        ],
        note: "Shown empty rather than hidden, so the layout does not shift when a source is added.",
      };

    case "energyFlow":
      return {
        title: "Energy flow",
        subtitle: "Where the power is going, from the reading rather than from an animation.",
        facts: [
          reading === null
            ? "Panels: no reading"
            : `From the panels: ${formatUnit(reading.pvPowerW, "W", true)}`,
          reading === null
            ? "Grid: no reading"
            : `From the grid: ${formatUnit(reading.gridPowerW, "W", true)}`,
          reading === null
            ? "Home: no reading"
            : `To the house: ${formatUnit(reading.loadPowerW, "W", true)}`,
          reading === null
            ? "Battery: no reading"
            : `Battery: ${directionOf(reading.batteryPowerW)} at ${formatUnit(Math.abs(reading.batteryPowerW), "W", true)}`,
        ],
        note: source,
      };

    default:
      return {
        title: "Widget",
        subtitle: "No detail is recorded for this widget.",
        facts: [],
        note: source,
      };
  }
}

function directionOf(batteryPowerW: number): string {
  if (batteryPowerW < 0) return "Charging";
  if (batteryPowerW > 0) return "Discharging";
  return "Idle";
}
